import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { RedirectService } from 'src/app/core/services/redirect/redirect.service';
import { AuthorizationService } from 'src/app/core/services/authorization/authorization.service';
import { AlertBoxComponent } from '../alert-box/alert-box.component';
import { ConfigService } from 'src/app/core/services/config.service';
import { Router } from '@angular/router';
import { Subscription, VirtualTimeScheduler } from 'rxjs';
import { ClientService } from 'src/app/core/services/client/client.service';
import { User } from '../../models/user';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-module-block',
    templateUrl: './module-block.component.html',
    styleUrls: ['./module-block.component.scss'],
    standalone: false
})
export class ModuleBlockComponent implements OnInit {
  isClientSelected = false
  constructor(
    private authorizationService: AuthorizationService,
    private redirectService: RedirectService,
    public dialog: MatDialog,
    private configService: ConfigService,
    private authenticationService: AuthenticationService,
    private clientService: ClientService
  ) {
    this.supportEmail = this.configService.supportEmail;

    this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
      this.currentUser = user;
      this.setPermissions();
    });

    this.selectedClientSubscription = this.clientService.selectedClient.subscribe(client => {
      this.selectedClient = client;
      if (this.selectedClient) {
        this.isClientSelected = true;
      }
      this.setPermissions();
    });

    this.clientListSubscription = this.clientService.clientList.subscribe(clients => {
      this.clients = clients;
      this.setPermissions();
    });
  }

  @Input() module: any;
  @Output() outputAction = new EventEmitter<any>();
  supportEmail: string;

  currentUser: User;
  currentUserSubscription: Subscription;

  selectedClient: any;
  selectedClientSubscription: Subscription;

  clients: Array<any>;
  clientListSubscription: Subscription;

  settingPermissions = false;

  canAccessWaiver = false;
  isWaiverManagementModule = false;
  @Input() clientAccess = '';
  @Input() singleModuleTile: boolean;

  ngOnInit() {
    if (this.module !== undefined) {
      this.setPermissions();
      this.isWaiverManagementModule = (this.module.blockName === "Manage Students" || this.module.blockName === "Review Waivers" || this.module.blockName === "Schedule Email Notifications" || this.module.blockName === "Download Reports" || this.module.blockName === "Approve Coverage" || this.module.blockName === "View Policy Configuration");
    }
  }

  ngOnDestroy() {
    this.currentUserSubscription.unsubscribe();
    this.selectedClientSubscription.unsubscribe();
    this.clientListSubscription.unsubscribe();
  }

  hideOnDashboard(blockName) {
    const hideList = [
      'Enrollments',
      'Elections',
      'Submit Lists'
    ];
    return hideList.indexOf(blockName) > -1;
  }

  setPermissions() {
    if (this.module !== undefined) {
      if (this.module) {
        this.canAccessWaiver = !this.module.isAccessRestricted;
      }

      if (this.currentUser && this.module &&
        (
          (this.module.permission.indexOf('WaiverManagement') === -1) ||
          (this.module.permission.indexOf('WaiverManagement') > -1 && this.clients && this.clients.length > 0)
        )) {
        if (this.settingPermissions) {
          return;
        }

        this.settingPermissions = true;

        this.module.isAccessRestricted = this.isAccessRestricted(this.module);
        this.module.canAccess = this.canAccess(this.module);

        if (this.module.blocks !== undefined) {
          this.module.blocks.forEach(_module => {
            _module.isAccessRestricted = this.isAccessRestricted(_module);
            _module.canAccess = this.canAccess(_module);
          });
        }
        this.settingPermissions = false;
      }
    }
  }

  canAccess(block: any) {
    const blockObj = {
      permission: block.permission,
      default: block.default || ''
    };

    if (block.isEnrollmentRequired && block.isEarlyEnrollment) {
      if (this.selectedClient && this.selectedClient.EnrollmentEnabled && !this.selectedClient.EarlyEnrollment) {
        return false;
      }
    }

    if (block.blockName === 'Submit Coverage Elections' && this.isWaiverViewOnlyRole()) {
      if (this.selectedClient && this.selectedClient.EnrollmentEnabled && !this.selectedClient.EarlyEnrollment) {
        return true;
      }
    }

    return this.authorizationService.canAccess(blockObj);
  }

  isAccessRestricted(module) {
    const hasAccess = this.authorizationService.canAccessLink(module.permission);
    const defaultTxt = module.default || '';

    if (!this.isWaiverViewOnlyRole()) {
      return false;
    }

    if (defaultTxt.indexOf('always') > -1 && module.permission.indexOf('WaiverManagement / ') > -1) {

      // For Email Notification Enabled
      if (module.isEmailNotificationEnabled === true) {
        if (!hasAccess || (!this.isWaiverInternalAdminRole() && this.selectedClient && !this.selectedClient.EmailNotificationEnabled)) {
          return true;
        }
      }

      // For Auto Conversion Enabled
      if (module.isAutoConversionEnabled === true) {
        if (!hasAccess || (this.selectedClient && !this.selectedClient.AutoConversionEnabled)) {
          return true;
        }
      }

      if (module.isEnrollmentRequired && module.isEarlyEnrollment) { // Early Enrollment
        if (this.selectedClient) { // Selected Client
          if (this.selectedClient.EnrollmentEnabled && !this.selectedClient.EarlyEnrollment) {
            return false;
          } else if (!this.selectedClient.EnrollmentEnabled && !this.selectedClient.EarlyEnrollment) {
            return false;
          }
        } else {// Client List
          if (this.clients && this.clients.filter(client => client.EnrollmentEnabled && client.EarlyEnrollment).length > 0) {
            return false;
          }
        }
      } else if (module.isEnrollmentRequired && !module.isEarlyEnrollment) { // Coverage Election
        if (this.selectedClient) { // Selected Client
          if (this.selectedClient.EnrollmentEnabled && this.selectedClient.EarlyEnrollment) {
            return false;
          }
        } else { // Client List
          if (this.clients.filter(client => client.EnrollmentEnabled && !client.EarlyEnrollment).length > 0) {
            return false;
          }
        }
      } else if (!hasAccess) {
        return true;
      }
    }

    return !hasAccess;
  }

  isWaiverInternalAdminRole() {
    return this.currentUser.profile.Roles.indexOf('rWaiver Management Internal Admin') > -1;
  }

  isWaiverViewOnlyRole() {
    return this.currentUser.profile.Roles.indexOf('rWaiver Management View Only') > -1;
  }

  checkAndShowModal(defaultTxt) {
    if (defaultTxt.indexOf('check') > -1 && this.isWaiverViewOnlyRole()) {
      this.openDialog('AccessRestricted');
    } else {
      this.openDialog('FeatureNotEnabled');
    }
    return;
  }

  goToUrl(module) {
    // if(module.url === 'none')
    //   return;
    const hasAccess = this.authorizationService.canAccessLink(module.permission);
    const defaultTxt = module.default || '';

    if (defaultTxt.indexOf('always') > -1 && module.permission.indexOf('WaiverManagement / ') > -1) {

      // For Email Notification Enabled
      if (module.isEmailNotificationEnabled === true) {

        if (this.isWaiverViewOnlyRole() && !hasAccess) {
          this.openDialog('AccessRestricted');
          return;
        }

        if (!hasAccess || (!this.isWaiverInternalAdminRole() && this.selectedClient && !this.selectedClient.EmailNotificationEnabled)) {
          this.openDialog('FeatureNotEnabled');
          return;
        }
      }

      // For Auto Conversion Enabled
      if (module.isAutoConversionEnabled === true) {

        if (this.isWaiverViewOnlyRole() && !hasAccess) {
          this.openDialog('AccessRestricted');
          return;
        }

        if (!hasAccess || (this.selectedClient && !this.selectedClient.AutoConversionEnabled)) {
          this.openDialog('FeatureNotEnabled');
          return;
        }
      }

      if (module.isEnrollmentRequired && module.isEarlyEnrollment) { // Early Enrollment
        if (this.selectedClient) { // Selected Client
          if (this.selectedClient.EnrollmentEnabled && !this.selectedClient.EarlyEnrollment) {
            this.checkAndShowModal(defaultTxt);
            return;
          } else if (!this.selectedClient.EnrollmentEnabled && !this.selectedClient.EarlyEnrollment) { // Client List
            this.openDialog('FeatureNotEnabled');
            return;
          }
        } else {
          if (this.clients.filter(client => client.EnrollmentEnabled && client.EarlyEnrollment).length === 0) {
            this.checkAndShowModal(defaultTxt);
            return;
          }
        }
      } else if (module.isEnrollmentRequired && !module.isEarlyEnrollment) { // Coverage Election
        if (this.selectedClient) { // Selected Client
          if (this.selectedClient.EnrollmentEnabled && this.selectedClient.EarlyEnrollment) {
            this.checkAndShowModal(defaultTxt);
            return;
          }
        } else { // Client List
          if (this.clients.filter(client => client.EnrollmentEnabled && !client.EarlyEnrollment).length === 0) {
            this.checkAndShowModal(defaultTxt);
            return;
          }
        }
      } else if (!hasAccess) {
        this.openDialog('AccessRestricted');
        return;
      }
    }

    // Submit Coverage Elections
    if (module.blockName === 'Submit Coverage Elections' && this.isWaiverViewOnlyRole()) {
      this.openDialog('AccessRestricted');
      return;
    }
    if (!this.selectedClient && (module.permission.indexOf('WaiverManagement / ') > -1 || module.permission.indexOf('PaymentManagement / ') > -1)) {
      this.goToSelectClient(module);
      return;
    }
    this.redirectService.goToModule(module);
  }

  goToSelectClient(module) {
    this.outputAction.emit({
      isAutoConversionEnabled: module.isAutoConversionEnabled,
      isEnrollmentRequired: module.isEnrollmentRequired || false,
      isEarlyEnrollment: module.isEarlyEnrollment || true,
      isEmailNotificationEnabled: module.isEmailNotificationEnabled,
      transform: module.transform,
      url: module.url,
      module: module.permission,
      blockName: module.blockName
    });
  }

  openDialog(type: string): void {
    let alertBoxData = {};
    if (type === 'FeatureNotEnabled') {
      alertBoxData = {
        title: 'Notice',
        message: 'The feature has not been enabled for your school.<br/>Please contact <a href="mailto:' +
          this.supportEmail + '" title="Click here to email Partner Center Support">Partner Center Support</a> for assistance.'
      };
    } else if (type === 'AccessRestricted') {
      alertBoxData = {
        title: 'Notice',
        message: 'Access Restricted.'
      };
    }


    this.dialog.open(AlertBoxComponent, {
      width: '400px',
      data: alertBoxData,
      disableClose: false,
      autoFocus: false,
      panelClass: 'search-dialog-container'
    });

  }

}
